import { forwardRef, type ChangeEvent, type InputHTMLAttributes } from 'react';
import { useControllableState } from '../../hooks/useControllableState';
import './Radio.css';

export type RadioStatus = 'inactive' | 'active';

export type RadioVisualState = 'default' | 'hover' | 'pressed';

/** Radio button input. Supports controlled and uncontrolled modes via `status`/`defaultStatus`. */
export type RadioProps = {
  label?: string;
  /** Controlled checked status. When provided, the component is controlled. */
  status?: RadioStatus;
  /** Initial status for uncontrolled mode. */
  defaultStatus?: RadioStatus;
  disabled?: boolean;
  /**
   * Storybook-only visual state override to display hover/pressed static examples.
   * Keep undefined in real app usage.
   */
  visualState?: RadioVisualState;
  showFocus?: boolean;
  value?: string;
} & Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'checked' | 'disabled' | 'defaultValue'>;

export const Radio = forwardRef<HTMLInputElement, RadioProps>(function Radio({
  label = 'Radio button',
  status,
  defaultStatus = 'active',
  disabled = false,
  visualState,
  showFocus = false,
  className = '',
  id,
  value,
  ...rest
}, ref) {
  const controlledBool =
    status === 'active' ? true : status === 'inactive' ? false : undefined;
  const [checked, setChecked] = useControllableState(controlledBool, defaultStatus === 'active');

  const inputId = id ?? `spark-radio-${Math.random().toString(36).slice(2, 9)}`;
  const currentStatus: RadioStatus = checked ? 'active' : 'inactive';
  const hasLabel = label.length > 0;
  const onChangeFromProps = rest.onChange;

  const rootClasses = [
    'spark-radio',
    `spark-radio-${currentStatus}`,
    disabled ? 'spark-radio-disabled' : '',
    visualState ? `spark-radio-state-${visualState}` : '',
    showFocus ? 'spark-radio-show-focus' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (!disabled) {
      setChecked(true);
    }
    onChangeFromProps?.(event);
  };

  return (
    <label className={rootClasses} htmlFor={inputId}>
      <input
        ref={ref}
        id={inputId}
        className="spark-radio-input"
        type="radio"
        checked={checked}
        disabled={disabled}
        value={value}
        onChange={handleChange}
        {...rest}
      />
       <span className="spark-radio-control" aria-hidden="true">
        {checked ? (
          <span className="spark-radio-dot" />
        ) : (
          <svg
            className="spark-radio-plate"
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            aria-hidden="true"
          >
            <circle cx="7" cy="7" r="6.5" className="spark-radio-plate-shape" />
          </svg>
        )}
      </span>
      {hasLabel ? <span className="spark-radio-label">{label}</span> : null}
      <span className="spark-radio-focus" aria-hidden="true" />
    </label>
  );
});

Radio.displayName = 'Radio';
